import { useEffect } from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import HomeDiseaseSymptomSearch from '@/components/templates/mobile/home/HomeSearchTemplate/components/HomeDiseaseSymptomSearch';
import { zIndex } from '@/constants/zIndex';
import DNSearchHeader from '@/designComponents/molecules/DNSearchHeader';
import useSearchDoctorViewModel from '@/hooks/search/doctor/useSearchDoctorViewModel';
import useTotalSearchHospitalViewModel from '@/hooks/search/hospital/useSearchHospitalViewModel';
import useSearchMagazineViewModel from '@/hooks/search/magazine/useSearchMagazineViewModel';
import useSearchQuestionViewModel from '@/hooks/search/qna/useSearchQuestionViewModel';
import useHomeSearchViewModel from '@/hooks/search/useHomeSearchViewModel';
import useScreenFocus from '@/hooks/useScreenFocus';
import useToast from '@/hooks/useToast';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

import useSearchDiseaseEncyclopediaViewModel from '@/hooks/search/disease-encyclopedia/useSearchDiseaseEncyclopediaViewModel';
import useSearchMedicineDictionaryViewModel from '@/hooks/search/medicine-dictionary/useSearchMedicineDictionaryViewModel';
import useModal from '@/hooks/useModal';
import SearchResult from './components/SearchResult';

interface Props extends CustomPageProps {
  entryType: string;
}

const HomeSearchTemplate = ({ client, entryType }: Props) => {
  useScreenFocus();
  const { showToast } = useToast();
  const { showModal } = useModal();
  const viewModel = useHomeSearchViewModel({ client, entryType });
  const qnaViewModel = useSearchQuestionViewModel({
    keyword: viewModel.keyword,
    tab: viewModel.selectedTabValue,
  });
  const magazineViewModel = useSearchMagazineViewModel({
    keyword: viewModel.keyword,
    tab: viewModel.selectedTabValue,
  });
  const doctorViewModel = useSearchDoctorViewModel({
    homeSearchCategory: viewModel.category,
    keyword: viewModel.keyword,
    tab: viewModel.selectedTabValue,
  });
  const hospitalViewModel = useTotalSearchHospitalViewModel({
    keyword: viewModel.keyword,
    tab: viewModel.selectedTabValue,
    latitude: viewModel.coordinate.latitude,
    longitude: viewModel.coordinate.longitude,
  });
  const medicineDictionaryViewModel = useSearchMedicineDictionaryViewModel({
    keyword: viewModel.keyword,
    tab: viewModel.selectedTabValue,
  });
  const diseaseEncyclopediaViewModel = useSearchDiseaseEncyclopediaViewModel({
    keyword: viewModel.keyword,
    tab: viewModel.selectedTabValue,
  });

  useEffect(() => {
    if (!viewModel.isSnackBarOpen) {
      return;
    }

    showToast({
      message: '소중한 의견을 남겨주셔서 감사합니다.',
    });
  }, [viewModel.isSnackBarOpen]);

  useEffect(() => {
    if (!viewModel.modal.isOpen) {
      return;
    }

    showModal({
      ...viewModel.modal.info,
    });
  }, [viewModel.modal]);

  return (
    <TemplateLayout.Wrapper>
      <TemplateLayout.Content>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbs.Wrapper>
            <BreadCrumbs visible name='통합검색' type='search' path={`${ROUTES.SEARCH().HOME.path}`} />
          </BreadCrumbs.Wrapper>
        )}
        <SearchBarWrapper>
          <DNSearchHeader
            placeholder={entryType === 'symptom' ? '감기, 코로나, 목이 따가워요' : '증상, 질병, 병원을 검색해보세요'}
            shouldShowResetButton={!!viewModel.keyword}
            searchInputRef={viewModel.searchInputRef}
            onSubmit={viewModel.handleChangeKeyword}
            onReset={viewModel.handleResetSearchKeyword}
            onClickBackButton={viewModel.handleBackButtonClick}
          />
        </SearchBarWrapper>

        {!viewModel.isSearchResultShow && (
          <HomeDiseaseSymptomSearch
            entryType={entryType}
            popularSymptomList={viewModel.popularSymptomList}
            handleSymptomKeywordClick={viewModel.handleSymptomKeywordClick}
            handleDeleteItemRecentKeyword={viewModel.handleDeleteItemRecentKeyword}
            handleDeleteAllRecentKeyword={viewModel.handleDeleteAllRecentKeyword}
            handleSearchDirectKeyword={viewModel.handleSearchDirectKeyword}
            recentKeywordViewData={viewModel.recentKeywordViewData}
            client={client}
            bannerAdExchange={viewModel.bannerAdExchange}
            onImpressionAdLog={viewModel.handleSendExposureLog}
          />
        )}

        {viewModel.isSearchResultShow && (
          <SearchResult
            viewModel={viewModel}
            qnaViewModel={qnaViewModel}
            magazineViewModel={magazineViewModel}
            doctorViewModel={doctorViewModel}
            isMobileWeb={client.clientType === ClientType.MOBILE_WEB}
            hospitalViewModel={hospitalViewModel}
            medicineDictionaryViewModel={medicineDictionaryViewModel}
            diseaseEncyclopediaViewModel={diseaseEncyclopediaViewModel}
          />
        )}
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default HomeSearchTemplate;

const SearchBarWrapper = styled.div`
  position: sticky;
  z-index: ${zIndex.AppHeader};

  ${({ theme }) => `
    top: ${theme.client.clientType === ClientType.MOBILE_WEB ? theme.size.mobileWebHeader.height : 0}px;
  `};
`;
